import {
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
  Validators as BaseValidators,
} from '@angular/forms';
import {
  containsText,
  equals,
  isDefined,
  Validation,
  ValidationMessage,
} from '@softline/core';

export interface ValidationExtras {
  validation?: Validation;
  success?: boolean;
  successValidation?: Validation;
}

export namespace Validators {
  export function min(min: number, extras?: ValidationExtras): ValidatorFn {
    return (control: AbstractControl) => {
      let result = BaseValidators.min(min)(control);
      if (result && isDefined(result['min']))
        result['min'] = extras?.validation ?? {
          isValid: false,
          messages: [
            {
              type: 'error',
              subject: '#UI_CORE.VALIDATION.MIN.SUBJECT',
              message: '#UI_CORE.VALIDATION.MIN.MESSAGE',
              data: result['min'],
            },
          ],
        };
      else if (extras?.success) {
        result = { ...(result ?? {}) };
        result['min'] = extras?.successValidation ?? {
          isValid: true,
          messages: [
            {
              type: 'success',
              subject: '#UI_CORE.VALIDATION.MIN.SUBJECT',
              message: '#UI_CORE.VALIDATION.MIN.MESSAGE',
              data: { min },
            },
          ],
        };
      }
      return result;
    };
  }

  export function max(max: number, extras?: ValidationExtras): ValidatorFn {
    return (control: AbstractControl) => {
      let result = BaseValidators.max(max)(control);
      if (result && isDefined(result['max']))
        result['max'] = extras?.validation ?? {
          isValid: false,
          messages: [
            {
              type: 'error',
              subject: '#UI_CORE.VALIDATION.MAX.SUBJECT',
              message: '#UI_CORE.VALIDATION.MAX.MESSAGE',
              data: result['max'],
            },
          ],
        };
      else if (extras?.success) {
        result = { ...(result ?? {}) };
        result['max'] = extras?.successValidation ?? {
          isValid: true,
          messages: [
            {
              type: 'success',
              subject: '#UI_CORE.VALIDATION.MAX.SUBJECT',
              message: '#UI_CORE.VALIDATION.MAX.MESSAGE',
              data: { max },
            },
          ],
        };
      }
      return result;
    };
  }

  export function required(extras?: ValidationExtras): ValidatorFn {
    return (control: AbstractControl) => {
      let result = BaseValidators.required(control);
      if (result && isDefined(result['required']))
        result['required'] = extras?.validation ?? {
          isValid: false,
          messages: [
            {
              type: 'error',
              subject: '#UI_CORE.VALIDATION.REQUIRED.SUBJECT',
              message: '#UI_CORE.VALIDATION.REQUIRED.MESSAGE',
            },
          ],
        };
      else if (extras?.success) {
        result = { ...(result ?? {}) };
        result['required'] = extras?.successValidation ?? {
          isValid: true,
          messages: [
            {
              type: 'success',
              subject: '#UI_CORE.VALIDATION.REQUIRED.SUBJECT',
              message: '#UI_CORE.VALIDATION.REQUIRED.MESSAGE',
            },
          ],
        };
      }
      return result;
    };
  }

  export function email(extras?: ValidationExtras): ValidatorFn {
    return (control: AbstractControl) => {
      let result = BaseValidators.email(control);
      if (result && isDefined(result['email']))
        result['email'] = extras?.validation ?? {
          isValid: false,
          messages: [
            {
              type: 'error',
              subject: '#UI_CORE.VALIDATION.EMAIL.SUBJECT',
              message: '#UI_CORE.VALIDATION.EMAIL.MESSAGE',
            },
          ],
        };
      else if (extras?.success) {
        result = { ...(result ?? {}) };
        result['email'] = extras?.successValidation ?? {
          isValid: true,
          messages: [
            {
              type: 'success',
              subject: '#UI_CORE.VALIDATION.EMAIL.SUBJECT',
              message: '#UI_CORE.VALIDATION.EMAIL.MESSAGE',
            },
          ],
        };
      }
      return result;
    };
  }

  export function multiEmail(separator = ',', extras?: ValidationExtras): ValidatorFn {
    return (control: AbstractControl) => {
      let result: ValidationErrors | null = null;

      const validateEmails = (emailString: string) => {
        const emails = emailString.split(separator).map(s => {return s.trim()});
        const emailWithError = emails
          .map((email) => BaseValidators.email({ value: email } as any))
          .find((_) => {
            return _ !== null;
          })
        return emailWithError === undefined;
      };

      if (control.value === '' || !control.value || validateEmails(control.value)) {
        result = {};
        result['multiEmail'] = extras?.successValidation ?? {
          isValid: true,
          messages: [
            {
              type: 'success',
              subject: '#UI_CORE.VALIDATION.EMAIL.SUBJECT',
              message: '#UI_CORE.VALIDATION.EMAIL.MESSAGE',
            },
          ],
        };
      } else {
        result = {};
        result['multiEmail'] = extras?.validation ?? {
          isValid: false,
          messages: [
            {
              type: 'error',
              subject: '#UI_CORE.VALIDATION.EMAIL.SUBJECT',
              message: '#UI_CORE.VALIDATION.EMAIL.MESSAGE',
            },
          ],
        };
      }

      return result['multiEmail']?.isValid ? null : result
    };
  }

  export function minLength(minLength: number, extras?: ValidationExtras): ValidatorFn {
    return (control: AbstractControl) => {
      let result = BaseValidators.minLength(minLength)(control);
      if (result && isDefined(result['minlength']))
        result['minlength'] = extras?.validation ?? {
          isValid: false,
          messages: [
            {
              type: 'error',
              subject: '#UI_CORE.VALIDATION.MIN_LENGTH.SUBJECT',
              message: '#UI_CORE.VALIDATION.MIN_LENGTH.MESSAGE',
              data: result['minlength'],
            },
          ],
        };
      else if (extras?.success) {
        const actualLength = control.value?.length;
        result = { ...(result ?? {}) };
        result['minlength'] = extras?.successValidation ?? {
          isValid: true,
          messages: [
            {
              type: 'success',
              subject: '#UI_CORE.VALIDATION.MIN_LENGTH.SUBJECT',
              message: '#UI_CORE.VALIDATION.MIN_LENGTH.MESSAGE',
              data: { actualLength, requiredLength: minLength },
            },
          ],
        };
      }
      return result;
    };
  }

  export function maxLength(maxLength: number, extras?: ValidationExtras): ValidatorFn {
    return (control: AbstractControl) => {
      let result = BaseValidators.maxLength(maxLength)(control);
      if (result && isDefined(result['maxlength']))
        result['maxlength'] = extras?.validation ?? {
          isValid: false,
          messages: [
            {
              type: 'error',
              subject: '#UI_CORE.VALIDATION.MAX_LENGTH.SUBJECT',
              message: '#UI_CORE.VALIDATION.MAX_LENGTH.MESSAGE',
              data: result['maxlength'],
            },
          ],
        };
      else if (extras?.success) {
        const actualLength = control.value?.length;
        result = { ...(result ?? {}) };
        result['maxlength'] = extras?.successValidation ?? {
          isValid: true,
          messages: [
            {
              type: 'success',
              subject: '#UI_CORE.VALIDATION.MIN_LENGTH.SUBJECT',
              message: '#UI_CORE.VALIDATION.MIN_LENGTH.MESSAGE',
              data: { actualLength, requiredLength: maxLength },
            },
          ],
        };
      }
      return result;
    };
  }

  export function isNumber(extras?: ValidationExtras): ValidatorFn {
    return (control: AbstractControl) => {
      let result: ValidationErrors | null = null;
      if (typeof control.value !== 'number' || Number.isNaN(control.value)) {
        result = {};
        result['isNumber'] = extras?.validation ?? {
          isValid: false,
          messages: [
            {
              type: 'error',
              subject: '#UI_CORE.VALIDATION.IS_NUMBER.SUBJECT',
              message: '#UI_CORE.VALIDATION.IS_NUMBER.MESSAGE',
            },
          ],
        };
      } else if (extras?.success) {
        result = {};
        result['isNumber'] = extras?.successValidation ?? {
          isValid: true,
          messages: [
            {
              type: 'success',
              subject: '#UI_CORE.VALIDATION.IS_NUMBER.SUBJECT',
              message: '#UI_CORE.VALIDATION.IS_NUMBER.MESSAGE',
            },
          ],
        };
      }
      return result;
    };
  }

  export function passwordValidator(options: {
    minLength: number;
    strengthLevel: 1 | 2 | 3 | 4;
  }): ValidatorFn {
    return (control: AbstractControl) => {
      let isValid = true;
      const lengthMessage: ValidationMessage = {
        type: 'success',
        subject: '#PASSWORD.VALIDATION.PASSWORD.MIN_LENGTH',
        message: '#PASSWORD.VALIDATION.PASSWORD.MIN_LENGTH_MESSAGE',
        data: {
          requiredLength: options.minLength,
          actualLength: control.value?.length ?? 0,
        },
      };
      const uppercaseMessage: ValidationMessage = {
        type: 'success',
        subject: '#PASSWORD.VALIDATION.PASSWORD.UPPERCASE',
        message: '',
      };
      const lowercaseMessage: ValidationMessage = {
        type: 'success',
        subject: '#PASSWORD.VALIDATION.PASSWORD.LOWERCASE',
        message: '',
      };
      const numberMessage: ValidationMessage = {
        type: 'success',
        subject: '#PASSWORD.VALIDATION.PASSWORD.NUMBER',
        message: '',
      };
      const symbolMessage: ValidationMessage = {
        type: 'success',
        subject: '#PASSWORD.VALIDATION.PASSWORD.SYMBOL',
        message: '',
      };

      if (!control.value || control.value.length < options.minLength)
        lengthMessage.type = 'error';

      if (!/[A-Z]/.exec(control.value)) uppercaseMessage.type = 'error';
      if (!/[a-z]/.exec(control.value)) lowercaseMessage.type = 'error';
      if (!/[0-9]/.exec(control.value)) numberMessage.type = 'error';
      if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.exec(control.value))
        symbolMessage.type = 'error';

      const actualStrengthLevel =
        (uppercaseMessage.type === 'success' ? 1 : 0) +
        (lowercaseMessage.type === 'success' ? 1 : 0) +
        (numberMessage.type === 'success' ? 1 : 0) +
        (symbolMessage.type === 'success' ? 1 : 0);

      if (actualStrengthLevel >= options.strengthLevel) {
        if (uppercaseMessage.type === 'error') {
          uppercaseMessage.type = 'info';
          uppercaseMessage.message = '#PASSWORD.VALIDATION.PASSWORD.OPTIONAL';
        }
        if (lowercaseMessage.type === 'error') {
          lowercaseMessage.type = 'info';
          lowercaseMessage.message = '#PASSWORD.VALIDATION.PASSWORD.OPTIONAL';
        }
        if (numberMessage.type === 'error') {
          numberMessage.type = 'info';
          numberMessage.message = '#PASSWORD.VALIDATION.PASSWORD.OPTIONAL';
        }
        if (symbolMessage.type === 'error') {
          symbolMessage.type = 'info';
          symbolMessage.message = '#PASSWORD.VALIDATION.PASSWORD.OPTIONAL';
        }
      }

      if (actualStrengthLevel < options.strengthLevel || lengthMessage.type === 'error')
        isValid = false;

      const result: Validation = {
        isValid,
        messages: [
          lengthMessage,
          uppercaseMessage,
          lowercaseMessage,
          numberMessage,
          symbolMessage,
        ],
      };
      return { password: result };
    };
  }

  export function passwordConfirmValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      if (!control.value) return null;

      if (control.value !== control.parent?.value?.password)
        return {
          passwordConfirm: {
            isValid: false,
            messages: [
              {
                type: 'error',
                subject: '#PASSWORD.VALIDATION.PASSWORD_REPEAT.UNEQUAL',
              },
            ],
          },
        };
      return null;
    };
  }

  export class Matcher<T extends object> {
    private isNot = false;

    constructor(private property: keyof T) {}

    not(): this {
      this.isNot = true;
      return this;
    }

    toBe(value: any): ValidatorFn {
      const func = (o: T) => o[this.property] === value;
      return this.toValidatorFn(func);
    }
    toEqual(value: any): ValidatorFn {
      const func = (o: T) => equals(o[this.property], value);
      return this.toValidatorFn(func);
    }
    toContain(text: string, ignoreCase?: boolean): ValidatorFn {
      const func = (o: T) => containsText(o[this.property], text, ignoreCase);
      return this.toValidatorFn(func);
    }
    toBeLessThan(value: any): ValidatorFn {
      const func = (o: T) => o[this.property] < value;
      return this.toValidatorFn(func);
    }
    toBeLessThanOrEqual(value: any): ValidatorFn {
      const func = (o: T) => o[this.property] <= value;
      return this.toValidatorFn(func);
    }
    toBeGreaterThan(value: any): ValidatorFn {
      const func = (o: T) => o[this.property] > value;
      return this.toValidatorFn(func);
    }
    toBeGreaterThanOrEqual(value: any): ValidatorFn {
      const func = (o: T) => o[this.property] >= value;
      return this.toValidatorFn(func);
    }
    toBeUndefined(): ValidatorFn {
      const func = (o: T) => o[this.property] === undefined;
      return this.toValidatorFn(func);
    }

    toBeProperty(name: keyof T): ValidatorFn {
      const func = (o: T) => o[this.property] === o[name];
      return this.toValidatorFn(func);
    }
    toEqualProperty(name: keyof T): ValidatorFn {
      const func = (o: T) => equals(o[this.property], o[name]);
      return this.toValidatorFn(func);
    }
    toContainProperty(name: keyof T, ignoreCase?: boolean): ValidatorFn {
      const func = (o: T) =>
        containsText(o[this.property], o[name] as unknown as string, ignoreCase);
      return this.toValidatorFn(func);
    }
    toBeLessThanProperty(name: keyof T): ValidatorFn {
      const func = (o: T) => o[this.property] < o[name];
      return this.toValidatorFn(func);
    }
    toBeLessThanOrEqualProperty(name: keyof T): ValidatorFn {
      const func = (o: T) => o[this.property] <= o[name];
      return this.toValidatorFn(func);
    }
    toBeGreaterThanProperty(name: keyof T): ValidatorFn {
      const func = (o: T) => o[this.property] > o[name];
      return this.toValidatorFn(func);
    }
    toBeGreaterThanOrEqualProperty(name: keyof T): ValidatorFn {
      const func = (o: T) => o[this.property] >= o[name];
      return this.toValidatorFn(func);
    }

    private toValidatorFn(
      func: (value: T) => boolean,
      validation?: Validation
    ): ValidatorFn {
      return (control: AbstractControl) => {
        let result = func(control.value);
        if (this.isNot) result = !result;
        if (!result)
          return {
            expect: validation ?? {
              isValid: false,
              messages: [
                {
                  type: 'error',
                  subject: '#UI_CORE.VALIDATION.REQUIRED.SUBJECT',
                  message: '#UI_CORE.VALIDATION.REQUIRED.MESSAGE',
                },
              ],
            },
          };
        return {};
      };
    }
  }
}
